import axios from 'axios'

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  headers: { 'Content-Type': 'application/json' },
})

// Attach auth token + shop domain on every request
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token')
  const shopDomain = localStorage.getItem('shop_domain')
  if (token) config.headers.Authorization = `Bearer ${token}`
  if (shopDomain) config.headers['X-Shop-Domain'] = shopDomain
  return config
})

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const autoLogin = (shop: string, host: string, idToken?: string) =>
  apiClient.post('/api/shopify/auto-login', { shop, host, id_token: idToken })

// ─── Forms ────────────────────────────────────────────────────────────────────

export const getForms = () =>
  apiClient.get('/api/forms')

export const getForm = (id: number) =>
  apiClient.get(`/api/forms/${id}`)

export const createForm = (data: object) =>
  apiClient.post('/api/forms', data)

export const updateForm = (id: number, data: object) =>
  apiClient.put(`/api/forms/${id}`, data)

export const deleteForm = (id: number) =>
  apiClient.delete(`/api/forms/${id}`)

export const duplicateForm = (id: number) =>
  apiClient.post(`/api/forms/${id}/duplicate`)

export const generateFormKey = () =>
  apiClient.post('/api/forms/generate-key')

// ─── Submissions ──────────────────────────────────────────────────────────────

export const getSubmissions = (formId: number, params?: object) =>
  apiClient.get(`/api/forms/${formId}/submissions`, { params })

export const deleteSubmission = (submissionId: number) =>
  apiClient.delete(`/api/forms/submissions/${submissionId}`)

export const bulkDeleteSubmissions = (formId: number, ids: number[]) =>
  apiClient.delete(`/api/forms/${formId}/submissions/bulk`, { data: { ids } })

export const exportSubmissions = (formId: number) =>
  apiClient.get(`/api/forms/${formId}/submissions/export`, { responseType: 'blob' })

export const updateSubmissionStatus = (submissionId: number, status: string) =>
  apiClient.patch(`/api/forms/submissions/${submissionId}/status`, { status })

export const approveSubmission = (submissionId: number, note?: string) =>
  apiClient.post(`/api/forms/submissions/${submissionId}/approve`, { note })

export const rejectSubmission = (submissionId: number, note?: string) =>
  apiClient.post(`/api/forms/submissions/${submissionId}/reject`, { note })

export const anonymizeSubmission = (submissionId: number) =>
  apiClient.post(`/api/forms/submissions/${submissionId}/anonymize`)

// ─── Analytics ────────────────────────────────────────────────────────────────

export const getFormAnalytics = (formId: number, params?: object) =>
  apiClient.get(`/api/forms/${formId}/analytics`, { params })

export const getStoreAnalytics = (days = 30) =>
  apiClient.get('/api/forms/analytics', { params: { days } })

// ─── Store info (shop name, plan) ──────────────────────────────────────────────

export const getStoreInfo = () =>
  apiClient.get('/api/shopify/validate')

export const getStoreLanguages = () =>
  apiClient.get('/api/shopify/languages')

// ─── Plans ────────────────────────────────────────────────────────────────────

export const getPlans = () =>
  apiClient.get('/api/shopify/plans')

export const subscribePlan = (planId: number, billingCycle: 'monthly' | 'yearly' = 'monthly') =>
  apiClient.post('/api/shopify/subscribe-plan', {
    shop: localStorage.getItem('shop_domain'),
    host: localStorage.getItem('shop_host'),
    plan: planId,
    billingCycle,
  })

export default apiClient
